// src/hooks/useLiveVisitors.ts
//
// Polls `GET /pixel-sites/live-visitors` for the "Live on your site" card and
// the pixel install panel (plan 2026-10-05 verity pixel dashboard + live
// sessions, section B, step B3).
//
// Rules this hook owns, so no consumer re-implements them:
//
//   - The polling itself is usePolledResource, scoped to the active account
//     AND the window: every 5 s (the 5-minute window) or 10 s (the 30-minute
//     one, plan 2026-10-07 live card) while mounted AND the tab is visible
//     (nothing polls while nobody looks, which is what keeps the backend's
//     Pub/Sub pulls at zero), never overlapping, and an answer that lands
//     after an account or window switch or an unmount is dropped. A switch
//     also resets the backoff below.
//   - `no_pixel` backs off to one check per minute: an account without a pixel
//     has nothing to count, but may install one while the page is open.
//   - `unavailable` and a failed request keep the LAST number (status "lost"),
//     so the card can show it dimmed instead of dropping to nothing.
//   - Without the account flag `live_visitors` nothing polls and the answer
//     is `no_pixel`, so every consumer renders nothing (plan 2026-10-11
//     live-visitors-behind-a-feature-flag; the route refuses it too).

import {
  getLiveVisitors,
  type LiveVisitors,
  type LiveWindowSeconds,
} from "@/api/pixelSites";
import { useAuth } from "@/context/AuthContext";
import { useFeatureFlag } from "@/hooks/useFeatureFlag";
import { usePolledResource, type PollOutcome } from "@/hooks/usePolledResource";

export const LIVE_VISITORS_POLL_MS = 5_000;
/** The 30-minute view refreshes less often (Arjen, 2026-10-07). */
export const LIVE_VISITORS_LONG_POLL_MS = 10_000;
export const LIVE_VISITORS_NO_PIXEL_RECHECK_MS = 60_000;

export type LiveVisitorsStatus =
  /** No answer yet for this account. */
  | "loading"
  /** No active pixel site (or no account): render nothing. */
  | "no_pixel"
  | "setting_up"
  | "available"
  /** `unavailable` or a failed request; `last` holds the previous number, if any. */
  | "lost";

export type LiveVisitorsView = {
  status: LiveVisitorsStatus;
  /** The most recent AVAILABLE answer for this account, kept through "lost". */
  last: LiveVisitors | null;
};

const LOADING: LiveVisitorsView = { status: "loading", last: null };
const NO_ACCOUNT: LiveVisitorsView = { status: "no_pixel", last: null };

/** `unavailable` (or an unknown reason) reads as a failed poll: keep the last number. */
function isLost(res: LiveVisitors): boolean {
  return (
    !res.available && res.reason !== "no_pixel" && res.reason !== "setting_up"
  );
}

/** `no_pixel` backs off to one check per minute; every other answer resets it. */
function backoffMs(outcome: PollOutcome<LiveVisitors>): number | null {
  return outcome.ok &&
    !outcome.value.available &&
    outcome.value.reason === "no_pixel"
    ? LIVE_VISITORS_NO_PIXEL_RECHECK_MS
    : null;
}

export function useLiveVisitors(
  window: LiveWindowSeconds = 300,
): LiveVisitorsView {
  const { activeAccount } = useAuth();
  const enabled = useFeatureFlag("live_visitors");
  const accountId = enabled ? (activeAccount?.id ?? null) : null;

  const polled = usePolledResource<LiveVisitors>({
    scopeKey: accountId ? `${accountId}:${window}` : null,
    fetch: () => getLiveVisitors(window),
    intervalMs:
      window === 1800 ? LIVE_VISITORS_LONG_POLL_MS : LIVE_VISITORS_POLL_MS,
    isFailure: isLost,
    backoffMs,
  });

  if (!accountId) return NO_ACCOUNT;
  const res = polled.data;
  if (polled.status === "pending" || !res) {
    return polled.status === "failed"
      ? { status: "lost", last: null }
      : LOADING;
  }
  if (polled.status === "failed") {
    return { status: "lost", last: res.available ? res : null };
  }
  if (res.available) return { status: "available", last: res };
  if (res.reason === "no_pixel") return { status: "no_pixel", last: null };
  return { status: "setting_up", last: null };
}
